/**
 * Library of functions to fix all the accessibility issues found in Kendo Grid
 */
const A11yGrid = (function () {
    return {
        enhanceKendoGridForA11y: _enhanceKendoGridForA11y,
        enhanceKendoSchedulerForA11y: _enhanceKendoSchedulerForA11y,
        moveMenuInDom: _moveMenuInDom,
        toggleGridRowMenu: _toggleGridRowMenu,
        fixExpandableDetails: _fixExpandableDetails
    };

    /**
     * Function to fix all of the non-accessible characteristics of a Kendo grid
     * @param {string} containerId - the ID of the Kendo <table>'s direct parent div
     * @param {number|string} rowHeaderColIndex - the index of the column that should be made into the row header (0-based index), or the data-field value on the column header
     * @param {number} - the zero-based index of the column containing the grid row checkboxes
     * @param {string} rowMenuContainerId - the ID of the menu container (i.e. the thing that opens when you click the menu button)
     */
    function _enhanceKendoGridForA11y(containerId, rowHeaderColIndex, checkboxColIndex, rowMenuContainerId) {
        let container = $('#' + containerId);
        let tableContainer = _wrapTableInScrollableContainer($(container));
        let table = $(tableContainer).find('table');

        _logDebug('Kendo grid container:', container);

        $(table).addClass('a11y-k-grid');

        _addRowHeader(table, rowHeaderColIndex);
        _addEmptyHrefs(table);
        _convertPhoneNumbersToLinks(table);
        _convertEmailsToLinks(table);
        _fixCheckboxes(table, containerId, checkboxColIndex);
        _fixStatusIcons(table);
        _fixTableHeader(table);
        _removeUnnecessaryTableRoles(table);
        setTimeout(function () {
            _fixExpandableDetails(table, containerId);
        });

        if (rowMenuContainerId) {
            _fixGridRowMenu(table, rowMenuContainerId);
        }

        // enhance the kendo pagination controls, but only if they are being used
        // (GC also implemented an "ahs-pager" which might be in use - we don't want to fix that here)
        let pagerContainer = $('#' + containerId + ' .a11y-k-grid-container + .k-grid-pager');
        setTimeout(function () {
            // this is in a timeout because converting the k-pager into ahs-pager is on a 100ms timeout (in main.ts)
            if ($(pagerContainer).find('.k-pager-nav').length > 0) {
                // there is a possibility of grids with duplicate IDs in the DOM (see GCPROD-23528), so fix the pagination on each one found in the DOM
                $(pagerContainer).each(function (index, _pagerContainer) {
                    if (!$.fn.isNullOrEmpty(_pagerContainer)) {
                        _fixKendoGridPagination(_pagerContainer, containerId, index);
                    }
                });
            }
        }, 200);
    }

    /**
     * The Kendo scheduler is technically a table/grid, so we have to abide by some of the A11y rules of grids. Although
     * there is only one instance of the Kendo Scheduler being used (the My Calendar widget), we are including a function
     * for remediating that scheduler here, to keep it together with the other standard grid fixes. There are also lots
     * of embedded <table> elements on this thing... not sure if we'll be able to make it pass a11y.
     * @param {any} containerId
     */
    function _enhanceKendoSchedulerForA11y(containerId) {
        let container = $('#' + containerId);
        let layoutTable = $(container).find('.k-scheduler-layout');
        let timesTable = $(layoutTable).find('.k-scheduler-times table.k-scheduler-table');
        let contentTable = $(layoutTable).find('.k-scheduler-content table.k-scheduler-table');

        _logDebug('enhancing Kendo scheduler [' + containerId + ']', container);

        // remove role="presentation" from calendar table
        $(layoutTable).removeAttr('role');
        $(timesTable).removeAttr('role');
        $(contentTable).removeAttr('role');

        // the Kendo scheduler table seems to always only make use of two rows on the outer table - the first row is the "header",
        // and the second row is for the calendar day/week/month slots. Convert the first row's <td> tags into <th scope="col"> here:
        let layoutTableRow1 = $(layoutTable).find('> tbody:nth-child(1) > tr:nth-child(1)');
        $(layoutTableRow1).find('td').changeElementType('th');
        $(layoutTableRow1).find('th').attr('scope', 'col');

        // the first column header does not have a label, it needs one for a11y
        $(layoutTableRow1).find('> th .k-scheduler-times th').text('Time');

        // the first column in every row of data (in this case, the 2nd row is "every" row) needs to be set as a <th scope="row"> - these are the times (7:00 AM, 8:00 AM, etc.)
        let layoutTableRow2 = $(layoutTable).find('> tbody:nth-child(1) > tr:nth-child(2)');
        $(layoutTableRow2).find('> td:nth-child(1)').changeElementType('th');
        $(layoutTableRow2).find('> th:nth-child(1)').attr('scope', 'row');

        // By default, the k-scheduler-fullday button is located at the bottom of the calendar - in the footer.
        // However, UX wants it moved to the toolbar (at the top) - so the following code moves the div containing the button from the footer to the toolbar.
        // The Kendo click event for the button depends upon the button residing within a div containing the k-scheduler-footer class - so we have to
        // move the entire footer div.
        // Also, we move the div/button so that the tab ordering is correct.
        // Once moved, css styling makes it look pretty.
        let divSchedulerFooter = $('#' + containerId).find("div.k-scheduler div.k-scheduler-footer.k-toolbar:not(.divToolbarShowButton)");
        if (divSchedulerFooter && divSchedulerFooter.length > 0) {
            // Kendo has created a new footer containing the k-scheduler-fullday div.
            let divToolbarShowButton = $('#' + containerId).find("div.k-scheduler div.k-scheduler-footer.k-toolbar.divToolbarShowButton");
            divToolbarShowButton.remove();      // Remove the old button

            // Move the button before the k-scheduler-views-wrapper span
            let viewsWrapperSpan = $('#' + containerId).find("span.k-scheduler-views-wrapper");
            if (viewsWrapperSpan && viewsWrapperSpan.length > 0) {
                divSchedulerFooter.insertBefore(viewsWrapperSpan);
                divSchedulerFooter.addClass('divToolbarShowButton');
                _logDebug('Moved the k-scheduler-footer div', null, true);
            }

        }

        // set aria-current on the Day/Week/Month buttons based on which one is selected
        $(container).find('.k-scheduler-views .k-button.k-state-selected').attr('aria-current', 'true');
        $(container).find('.k-scheduler-views .k-button:not(.k-state-selected)').attr('aria-current', 'false');

        $("#myCalendarHeader").attr("role", "region").attr("aria-label", "section header");
        $("#scheduler").removeAttr('role');

        // THE STUFF BELOW WAS DONE PRE-KENDO UPGRADE, AND WE HAVE NOT BEEN FLAGGED BY ALLYANT FOR IT (YET)...

        // adding aria-label.   The header is an ancestor and using the closest() selector didnt work b/c it isnt part of the DOM of the container var.
        // adding aria-label and screen reader messages
        //$(container).find("div.k-scheduler-toolbar").attr("role", "region").attr("aria-label", "my calendar filter").attr("aria-describedby", "srCalendarFilterSpan");
        //$(container).find("div.k-scheduler-toolbar").prepend("<span id='srCalendarFilterSpan' class='sr-only'>the following buttons will change the calendar view</span>");
        //$(container).find('li.k-nav-today > a').attr("aria-label", "show today").attr("aria-describedby", "srElementActivateMessage");
        //$(container).find('li.k-nav-prev > a').attr("aria-label", "show previous day").attr("aria-describedby", "srElementActivateMessage");
        //$(container).find('li.k-nav-next > a').attr("aria-label", "show next day").attr("aria-describedby", "srElementActivateMessage");
        //$(container).find('li.k-nav-current > a').attr("aria-label", "show date picker").attr("aria-describedby", "srElementChangeMessage");
        //$(container).find('li.k-view-day > a').attr("aria-label", "show daily calendar").attr("aria-describedby", "srElementActivateMessage");
        //$(container).find('li.k-view-week > a').attr("aria-label", "show weekly calendar").attr("aria-describedby", "srElementActivateMessage");
        //$(container).find('li.k-view-month > a').attr("aria-label", "show monthly calendar").attr("aria-describedby", "srElementActivateMessage");
    }

    /**
     * Show/hide the grid row menu (i.e. the vertical ellipsis menu on a grid row). This menu will only be displayed via
     * click/Enter, and will hide on mouseout, clicking on a menu item, or clicking the menu button again. This menu does
     * all the right things in terms of aria mark-up for accessibility. This function should be called for the onclick event
     * ellipsis button (see PHPatientAuthorizations.cshtml for example). In addition, the menu HTML itself should be
     * structured as an unordered list of buttons/links.
     *
     * If your menu option opens a dialog, remember that closing that dialog needs to return focus back to the triggering
     * menu button, so you may need to add a triggerBtnId param to the code displaying the dialog (see CPIndex.cshtml for e.g.)
     *
     * Button markup:
     * <div class='GearIconChkbox'>
     *   <input type='button' id='chkMemberPH_[uniqueID]' class='GearIconCls' onclick='A11yGrid.toggleGridRowMenu(this, "divPHAuthMenu_", ShowAuthSummaryMenu, GearIconMseOut)'/>
     * </div>
     *
     * Menu markup (add CSS class as per UX spec; use <button role="link"> for menu options which should behave like hyperlinks):
     * <div class="divClsRefralMessageMenu ContextChkMenu" id="divPHAuthMenu_" style="width: 175px; position: fixed; display: none;">
     *   <ul>
     *     <li><button id="divPHAuthAddNotes_">Add Notes</button></li>
     *     <li><button id="divPHAuthAddDocuments_">Add Documents</button></li>
     *     <li><button role="link" id="divPHAuthAddServiceLines_">Add Diagnosis/ Service lines</button></li>
     *   </ul>
     * </div>
     * @param {object} menuButton - the jquery element of the menu button (i.e. the vertical ellipsis button)
     * @param {string} menuContainerId - the ID of the menu container (i.e. the thing that opens when you click the menu button)
     * @param {method} showMenuCallback - function callback to display your menu
     * @param {method} hideMenuCallback - function callback to hide your menu
     * @param {string} altMenuContainerId - the ID of an alternate menu container (some grid row menus are implemented such that the menu container ID changes after it is displayed :( )
     */
    function _toggleGridRowMenu(menuButton, menuContainerId, showMenuCallback, hideMenuCallback, altMenuContainerId) {
        let menu = altMenuContainerId ? $('#' + altMenuContainerId) : $('#' + menuContainerId);
        let menuId = altMenuContainerId ? altMenuContainerId : menuContainerId;

        _logDebug('_toggleGridRowMenu()', { menu: menu, menuId: menuId, menuButton: menuButton, menuContainerId: menuContainerId, showMenuCallback: showMenuCallback, hideMenuCallback: hideMenuCallback, altMenuContainerId: altMenuContainerId });

        if ($(menu).is(':visible')) {
            // menu is open... close it:
            _logDebug('*** visible menu: calling _hideGridRowMenu() ***', null, true);
            _hideGridRowMenu(menuButton, menuId, hideMenuCallback);
        } else {
            // menu is closed... close any other open menus, and open this one
            let openMenuButton = $('.a11y-grid-row-options[aria-expanded="true"]').attr('aria-expanded', 'false');
            hideMenuCallback($(openMenuButton).attr('id'), menuId);

            _showGridRowMenu(menuButton, menu, showMenuCallback, altMenuContainerId);

            // close the menu if the user tabs out of the menu
            let firstMenuItem = document.querySelector('#' + menuId + ' > ul > li:first-of-type > button');
            let lastMenuItem = document.querySelector('#' + menuId + ' > ul > li:last-of-type > button');

            setTimeout(function () {
                $(menu).keydown(function (e) {
                    if ((e.key === 'Tab' && !e.shiftKey && e.target === lastMenuItem) || (e.key === 'Tab' && e.shiftKey && e.target === firstMenuItem) || e.key === 'Escape') {
                        _logDebug('*** keydown: calling _hideGridRowMenu() ***', null, true);
                        _hideGridRowMenu(menuButton, menuId, hideMenuCallback);
                    }
                });

                // close the menu if the mouse leaves the menu for longer than X ms
                if (!DevUtils.isDevMode()) {
                    let menuContainer = $(menu).closest('.GearIconChkbox') ? $(menu).closest('.GearIconChkbox') : $(menu);

                    $(menuContainer).mouseleave(function () {
                        setTimeout(function () {
                            _logDebug('*** menu mouseleave: calling _hideGridRowMenu() ***', null, true);
                            _hideGridRowMenu(menuButton, menuId, hideMenuCallback);
                        }, 50);
                    });
                }

                // close the menu when it is clicked on (i.e. user clicks a menu option)
                $(menu).click(function () {
                    _logDebug('*** menu option click: calling _hideGridRowMenu() ***', null, true);
                    _hideGridRowMenu(menuButton, menuId, hideMenuCallback);
                });
            }, 11);     /// 1ms longer than the timeout used in showGridRowMenu()
        }
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    /**
     * Many of the Kendo grid column defs that contain <a> links do not include an href (rather, they just have an onclick event handler),
     * which is required for a11y and to make them keyboard navigable. This function will attempt to find all instances of <a> missing the
     * href attribute, and add an empty one (i.e. <a href="javascript:void(0);">)
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _addEmptyHrefs(table) {
        _logDebug('_addEmptyHrefs(): fixing hrefs');

        let anchors = $(table).find('tbody tr a');
        if ($(anchors).length > 0) {
            $(anchors).each(function (index, anchor) {
                let href = $(anchor).attr('href');
                if (!href || href === '') {
                    _logDebug('_addEmptyHrefs(): fixing hrefs', anchor, true);
                    $(anchor).attr('href', 'javascript:void(0);').removeAttr('target');
                }
            });
        }
    }

    /**
     * Each row in a grid needs a column defined as a header <th scope="row">. Kendo does not include the
     * ability to do this out of the box, so we need to do it after the grid is rendered. This function will
     * change a <td> element --> <th scope="row"> element based on any column cell that has the ah-row-header
     * CSS class on it. You can do this with a Kendo Grid template like this:
     *
     * columns.Bound(o => o.FirstName).Title("First Name").HtmlAttributes(new { title = " #= FirstName #", @class = "ah-row-header" });
     *
     * @param {object} tableContainer - the jquery element of the direct parent of the Kendo <table> element
     * @param {number|string} rowHeaderColIndex - the index of the column to make a row header (0-based index), or the data-field value on the column header
     */
    function _addRowHeader(table, rowHeaderColIndex) {
        let dataFieldId;

        // if a string was passed in for the rowHeaderColIndex, assume it's the data-field value on the col header, and find the numeric index
        if (typeof rowHeaderColIndex === 'string') {
            dataFieldId = rowHeaderColIndex;
            let colHeader = $(table).find('thead th[data-field="' + dataFieldId + '"]');
            let colHeaders = $(table).find('thead th');
            rowHeaderColIndex = colHeaders.index(colHeader);
        }

        _logDebug('_addRowHeader(): creating <th> row headers on column ' + rowHeaderColIndex);

        let index = rowHeaderColIndex + 1;  // nth-child requires a 1-based index, so converting it here

        $(table).find('tbody tr td:nth-child(' + index + ')').changeElementType('th');
        $(table).find('tbody tr th').attr('scope', 'row');
    }

    /**
     * Function to convert text to an email hyperlink. In order for this to work the grid cell element containing the email address
     * must have a CSS class of 'ah-email'.
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _convertEmailsToLinks(table) {
        let emails = $(table).find('tbody tr .ah-email');
        if ($(emails.length > 0)) {
            $(emails).each(function (index, email) {
                $(email).html(A11yUtil.getHyperlinkEmail(email));
            });
        }
    }

    /**
     * Function to convert text to a telephone number hyperlink. In order for this to work the grid cell element containing the phone number
     * must have a CSS class of 'ah-telephone'.
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _convertPhoneNumbersToLinks(table) {
        let phoneNumbers = $(table).find('tbody tr .ah-telephone');
        if ($(phoneNumbers.length > 0)) {
            $(phoneNumbers).each(function (index, phoneNumber) {
                $(phoneNumber).html(A11yUtil.getHyperlinkTelephoneNumber(phoneNumber));
            });
        }
    }

    /**
     * The row selection checkboxes in the grids need to have labels associated with them. This function will dynamically add those
     * labels to both the header "select all" checkbox (if it exists), and the checkboxes in the table body.
     *
     * NOTE: we are using sr-only labels so as not to detract from the design by adding visible labels
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     * @param {string} containerId - ID of the grid container
     * @param {number} [checkboxColIndex] - optional zero-based index of the column where the checkbox is (will attempt to find a
     *                                      checkbox in col 1 if omitted)
     */
    function _fixCheckboxes(table, containerId, checkboxColIndex) {
        // look for a checkbox in the first column of the tbody, in case the call to enhanceKendoGridForA11y() did not include checkboxColIndex
        if (!checkboxColIndex && $(table).find('tbody tr td:nth-child(1) input[type="checkbox"]').length > 0) {
            checkboxColIndex = 0;
        }

        if (checkboxColIndex >= 0) {
            _logDebug('_fixCheckboxes() a11y-enhancing checkboxes in column [' + checkboxColIndex + ']');

            let index = checkboxColIndex + 1; // nth-child requires a 1-based index, so converting it here
            let labelName = containerId + '_checkboxLabel';

            if ($(table).find('th[data-ischeckboxcol="true"]').length === 0) {
                // add a data- attr so we can find it easier
                $(table).find('thead tr th:nth-child(' + index + ')').attr('data-ischeckboxcol', 'true');

                // add one label for all tbody checkboxes to reference via aria-labelledby
                $(table).before('<label id="' + labelName + '" class="sr-only">select row</label>');
            }

            // if there is a "select all" checkbox in the header, then add a sr-only label to it
            if ($(table).find('thead tr th input[type="checkbox"]').length > 0) {
                let headerLabelName = containerId + '_headerCheckboxLabel';
                let headerCheckboxId = $(table).find('thead tr th input[type="checkbox"]').attr('id');

                if ($('label[id="' + headerLabelName + '"]').length === 0) {
                    $(table).before('<label id="' + headerLabelName + '" for="' + headerCheckboxId + '" class="sr-only">, select all rows</label>');
                }
            }

            // for each checkbox in the body, add aria-labelledby attr
            $(table).find('tbody tr td:nth-child(' + index + ') input[type="checkbox"]').attr('aria-labelledby', labelName);
        } else {
            _logDebug('_fixCheckboxes() no checkboxes found in grid body');
        }

        // add a sr-only column header for the checkboxes column, if a checkbox exists in the header
        let tableHeaderCheckbox = $(table).find('thead tr th span.k-link input[type="checkbox"]');
        if ($(table).find('#rowSelectionHeader').length === 0 && $(tableHeaderCheckbox).length > 0) {
            _logDebug('_fixCheckboxes() adding sr-only label to checkbox column header');
            $(tableHeaderCheckbox).before('<span id="rowSelectionHeader" class="sr-only">row selection checkboxes</span>');
        }
    }

    /**
     * Fixes various a11y issues related to the vertical ellipsis grid row menus. See usage example in the _toggleGridRowMenu() function
     * for example of how the HTML markup should be structured for this to work.
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     * @param {string} rowMenuContainerId - the ID of the menu container (i.e. the thing that opens when you click the menu button)
     */
    function _fixGridRowMenu(table, rowMenuContainerId) {
        _logDebug('_fixGridRowMenu(): found and fixing grid row menus for rowMenuContainerId: ' + rowMenuContainerId);

        $(table).find('.GearIconCls').each(function () {
            $(this).attr({ 'aria-label': 'row options', 'aria-controls': rowMenuContainerId, 'aria-expanded': 'false' }).addClass('a11y-grid-row-options');
        });

        // there is only one instance of the menu container for grid rows, find it and add flag it with an identifying class
        let rowMenuContainer = $('#' + rowMenuContainerId);
        if (!$(rowMenuContainer).hasClass('ah-grid-row-menu')) {
            $(rowMenuContainer).addClass('ah-grid-row-menu');
        }
    }

    /**
     * Function to add sr-only text to icons in the grid representing status. This function will also convert
     * ornage fa-exclamation-circle icons to orange fa-exclamation-triangle icons.
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _fixStatusIcons(table) {
        if ($(table).find('.sr-status-icon').length === 0) {
            $(table).find('tbody td i.fa-exclamation-circle').each(function () {

                let parent = $(this).parent();
                let parentTitle = $(parent).attr('title');
                let srText;

                // we cannot use the fa-exclamation-circle for both "expired" and "expiring soon" (or "error" and "warning"), so change the expiring/warning to something differnt
                if ($(parent).hasClass('orange')) {
                    $(this).removeClass('fa-exclamation-circle').addClass('fa-exclamation-triangle');
                }

                // add aria-hidden="true" to the fontawesome icons
                $(this).attr('aria-hidden', 'true');

                // add sr-only text to the status icons, using either a) the title/tooltip of the parent span, if it exists, or b) something generic
                if (parentTitle) {
                    srText = parentTitle;
                } else if ($(parent).hasClass('orange')) {
                    srText = 'warning';
                } else {
                    // assume red/expired/error (we may need to add an "else if" condition later if we find another use case with yellow or some other color)
                    srText = 'error';
                }

                $(this).after('<span class="sr-only sr-status-icon">' + escapeHtml(srText) + '</span>');
            });
        }
    }

    /**
     * Function to make any changes necessary for the Kendo grid headers
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _fixTableHeader(table) {
        // using a timeout because Kendo will override our aria-sort if we don't wait until the event cycle is complete
        setTimeout(function () {
            $(table).find('th[data-role="columnsorter"]').each(function () {
                // change the sortable column header links to look like buttons to the screen reader
                $(this).find('a.k-link').attr('role', 'button');

                // add aria-sort="none" to sortable column headers, if it's not already sorted
                if ($(this).attr('aria-sort') === undefined) {
                    $(this).attr('aria-sort', 'none');
                }
            });
        });
    }

    /**
     * Function to remove unnecessary "role" attrs on the Kendo grids. Don't confuse this with _fixTableRoles() in A11yUtilities.js, which adds
     * role="presentation" on improper uses of <table> throughout the application.
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _removeUnnecessaryTableRoles(table) {
        let rolesToRemove = '[role="grid"], [role="row"], [role="rowgroup"], [role="gridcell"]';

        $(table).removeAttr('role').find(rolesToRemove).removeAttr('role');
    }

    /**
     * Function to make any changes necessary for the Kendo grid expandable/collapsible table rows
     * @param {object} table - the jquery element of the direct parent of the Kendo <table> element
     */
    function _fixExpandableDetails(table, gridId) {
        if ($(table).find('tbody td.k-hierarchy-cell').length > 0) {
            _logDebug('_fixExpandableDetails(): found and fixing expandable details for grid [' + gridId + ']');

            // for each expand button, do some a11y things...
            $(table).find('tbody tr.k-master-row').each(function (index, masterRow) {
                /* Add appropriate aria attritubtes to the expand button. We need to set aria-controls to the ID of the section that gets expanded
                 * (we are going to use the master row's data-uid value for that, since we know it ahead of time and it is unique for each row)
                 */
                let expandIcon = $(masterRow).find('td.k-hierarchy-cell a.k-icon');
                expandIcon.attr({ tabindex: '0', role: 'button', 'aria-label': 'more details', 'aria-expanded': 'false', 'aria-controls': $(masterRow).attr('data-uid') });
                if (expandIcon.find('.ah-kendo-expand-btn').length === 0) {
                    expandIcon.prepend('<span class="ah-kendo-expand-btn" aria-hidden="true"></span>');
                }
            });

            // update aria attrs when a row is expanded
            $('#' + gridId).data('kendoGrid').bind('detailExpand', function (e) {
                _logDebug('A11yGrid event detailExpand: gridId: ' + gridId, { e: e });

                // find the master row's data-uid value and set the detail row's ID to that (this is what we used for the aria-control attr on the expand button above)
                $(e.detailRow).attr('id', $(e.masterRow).attr('data-uid')).find('td.k-detail-cell').attr('role', 'presentation');

                // update aria-expanded attr to true on the expand button (we also need to force the aria-label back to 'more details' since Kendo changes it to 'Collapse' in this case - sigh)
                $(e.masterRow).find('td.k-hierarchy-cell a.k-icon').attr({ 'aria-expanded' : 'true', 'aria-label': 'more details' });
            });

            // update aria attrs when a row is collapsed
            $('#' + gridId).data('kendoGrid').bind('detailCollapse', function (e) {
                _logDebug('A11yGrid event detailCollapse: gridId: ' + gridId, { e: e });

                // update the aria-expanded attr to false on the expand button (we also need to force the aria-label back to 'more details' since Kendo changes it to 'Expand' in this case - sigh)
                $(e.masterRow).find('td.k-hierarchy-cell a.k-icon').attr({ 'aria-expanded': 'false', 'aria-label': 'more details' });
            });
        }
    }

    function _logDebug(message, object, isVerbose) {
        if (isVerbose) {
            DevUtils.logVerbose(message, 'A11yGrid', object, 'magenta');
        } else {
            DevUtils.logDebug(message, 'A11yGrid', object, 'magenta');
        }
    }

    /**
     * Function to fix all the a11y issues with the Kendo grid pagination feature. The pagination container
     * is located in the DOM just under <div class="k-grid"> and a sibling to <div class="a11y-k-grid-container">
     * @param {object} pagerContainer the container containing the kendo pagination controls
     * @param {string} containerId the ID of the grid container
     * @param {number} index zero-based index of the pagination component (in case of duplicates found in DOM)
     */
    function _fixKendoGridPagination(pagerContainer, containerId, index) {
        A11yUtil.fixKendoItemsPerPageBug(containerId);

        let firstPage = $(pagerContainer).find('a.k-pager-first');
        let previousPage = $(pagerContainer).find('a.k-pager-first + a.k-pager-nav');
        let pageNums = $(pagerContainer).find('ul.k-pager-numbers');
        let nextPage = $(pagerContainer).find('a.k-pager-first + a.k-pager-nav + div.k-pager-numbers-wrap + a.k-pager-nav');
        let lastPage = $(pagerContainer).find('a.k-pager-last');
        let pageInfo = $(pagerContainer).find('.k-pager-info.k-label');
        let refreshBtn = $(pagerContainer).find('a.k-pager-refresh');

        _logDebug('_fixKendoGridPagination(' + index + ') Kendo pagination container:', pagerContainer);
        _logDebug('_fixKendoGridPagination(' + index + '): adding aria-label attributes to pagination controls', [firstPage, pageNums, previousPage, nextPage, lastPage]);

        $(pagerContainer).addClass('a11y-k-grid-pager');

        // add aria-label to pager controls
        $(firstPage).attr('aria-label', 'Go to the first page').addClass('pager-nav');
        $(previousPage).attr('aria-label', 'Go to the previous page').addClass('pager-nav');
        $(pageNums).addClass('pager-nav');
        $(nextPage).attr('aria-label', 'Go to the next page').addClass('pager-nav');
        $(lastPage).attr('aria-label', 'Go to the last page').addClass('pager-nav');

        // add <nav> around the navigation elements of the pagination component
        if ($(pagerContainer).find('.ah-k-pager-nav').length === 0) {
            $(pagerContainer).find('.pager-nav').wrapAll('<nav aria-label="grid pagination" class="ah-k-pager-nav"></nav>');
        }

        // set the page number <ul> list element to presentational
        $(pageNums).attr('role', 'presentation');

        // move the "1 - x of y items" span so it comes BEFORE the refresh button in the DOM
        if ($(pagerContainer).find('.ah-k-pager-info').length === 0) {
            $(pagerContainer).find('.k-pager-info.k-label, a.k-pager-refresh').wrapAll('<span class="ah-k-pager-info"></span>');
        }
        let pageInfoHtml = $(pageInfo).length > 0 ? $(pageInfo)[0].outerHTML : '';
        let refreshBtnHtml = $(refreshBtn).length > 0 ? $(refreshBtn)[0].outerHTML : '';
        $(pagerContainer).find('.ah-k-pager-info').html(pageInfoHtml + refreshBtnHtml);

        // mark up the refresh hyperlink like a button
        // NOTE: The html change above changes the DOM. This requires that we find the refreshBtn again.
        refreshBtn = $(pagerContainer).find('a.k-pager-refresh');
        $(refreshBtn).attr('role', 'button');

        // make the entire pagination section a "region"
        $(pagerContainer).attr('role', 'region').attr('aria-label', 'section footer');

        // "unset" tabindex="-1" on the pager controls (not sure why Kendo would even do this)
        $(pagerContainer).find('.k-link').attr('tabindex', '0').removeAttr('aria-disabled');
        // now set tabindex="-1" on the disabled links (e.g. the "previous page" link is disabled when you're on page 1)
        $(pagerContainer).find('.k-link.k-state-disabled').attr('tabindex', '-1').attr('aria-disabled', 'true');
        // remove tabindex attribute of the selected page number
        $(pagerContainer).find('.k-link.k-state-selected').removeAttr('tabindex');

        /*
         * the "items per page" dropdown is a Kendo dropdown which is not accessible... remove the custom part and just use the
         * native select that is already there but hidden
         */
        let nativeSelect = $(pagerContainer).find('.k-pager-sizes select[data-role="dropdownlist"]');
        let nativeSelectId = containerId + '_pageSize';

        // display the native select (Kendo set it to display: none)
        nativeSelect.css('display', 'unset');
        // give it a special class we can customize later
        nativeSelect.addClass('ah-k-pager-select');
        // add ID to native select so we can associate a label to it
        nativeSelect.attr('id', nativeSelectId);
        // move the native select out of the part we're going to remove:
        nativeSelect.insertBefore($(pagerContainer).find('.k-pager-sizes .k-widget.k-dropdown'));

        // remove the Kendo custom select
        $(pagerContainer).find('.k-pager-sizes .k-dropdown-wrap').remove();
        $(pagerContainer).find('.k-pager-sizes .k-widget.k-dropdown').remove();

        // add a proper form field label (have to add a sr-only label here because trying to remove/replace the
        // floating "items per page" Kendo text proved difficult... doing so breaks the functionality of the dropdown)
        if ($(pagerContainer).find('.ah-k-pager-select-label').length === 0) {
            $(pagerContainer).find('.k-pager-sizes.k-label').prepend('<label for="' + nativeSelectId + '" class="ah-k-pager-select-label sr-only">Items per page</label>');
        }
    }

    /**
     * Function to wrap the Kendo <table></table> element in a <div id="containerID_table"></div> container.
     * This container will then be used to provide the sticky table header functionality
     * @param {object} container - the outer jquery element holding the entire Kendo table and pagination component
     */
    function _wrapTableInScrollableContainer(container) {
        // fix issue where overflow-y would not get honored on a11y-enhanced grids (caused by Kendo upgrade)
        $(container).css({ 'overflow-y': 'hidden', position: 'relative' });

        if ($(container).find('.a11y-k-grid-container').length === 0) {
            // only do this if the a11y-k-grid-container does not already exist (otherwise all kendo databound events will keep adding this div)
            let table = $(container).find('> table');
            if (table && table.length > 0) {
                $(table).wrapAll('<div id="' + $(container).attr('id') + '_table" class="a11y-k-grid-container"></div>');
            } else {
                DevUtils.logWarn('Could not find table to a11y-enhance, make sure the Kendo Scrollable property is set to false on the grid config', 'A11yGrid.wrapTableInScrollableContainer()');
            }
        }

        return $(container).find('.a11y-k-grid-container');
    }

    /*
     * GRID ROW MENU FUNCTIONS
     */
    function _hideGridRowMenu(menuButton, menuContainerId, hideMenuCallback, altMenuContainerId) {
        _logDebug('_hideGridRowMenu()', { menuButton: $(menuButton).attr('id'), menuContainerId: menuContainerId, hideMenuCallback: hideMenuCallback, altMenuContainerId: altMenuContainerId });

        hideMenuCallback($(menuButton).attr('id'), menuContainerId);
        $(menuButton).attr('aria-expanded', 'false');

        // put the menu back where it was in the DOM, or else it may be removed completely from the DOM if a navigation occurs
        let menu = ('#' + menuContainerId);
        let parentId = $(menu).attr('data-parentid');
        $(menu).detach().prependTo('#' + parentId);
        $(menu).hide();
    }

    function _showGridRowMenu(menuButton, menu, showMenuCallback, altMenuContainerId) {
        _logDebug('_showGridRowMenu()', { menu: menu, menuButton: $(menuButton).attr('id'), menuContainerId: $(menu).attr('id'), showMenuCallback: showMenuCallback, altMenuContainerId: altMenuContainerId });

        setTimeout(function () {
            showMenuCallback(menuButton);
            $(menuButton).attr('aria-expanded', 'true');

            // get parent DOM element so we know where to put the menu HTML after it is closed, and save it on the menu element for future
            let parent = $(menu).parent();
            let parentId = $(parent).attr('id');
            $(menu).attr('data-parentid', parentId);

            // we need to move the menu container just after the menu trigger button in the DOM so tabbing order works
            $(menu).insertAfter($(menuButton));

            if (!altMenuContainerId) {
                $(menu).show();
                _positionGridRowMenu(menuButton, menu);
            } else {
                let altMenu = $('#' + altMenuContainerId);
                setTimeout(function () {
                    // this is in a 110ms timeout to account for the .slideDown(100) duration in JsGlobal.ContextMenu_Show()
                    _positionGridRowMenu(menuButton, altMenu);
                }, 110);
            }
        }, 10);
    }

    function _moveMenuInDom(menuButton, menu) {
        // get parent DOM element so we know where to put the menu HTML after it is closed, and save it on the menu element for future
        let parent = $(menu).parent();
        let parentId = $(parent).attr('id');
        $(menu).attr('data-parentid', parentId);

        // we need to move the menu container just after the menu trigger button in the DOM so tabbing order works
        $(menu).insertAfter($(menuButton));
    }

    function _positionGridRowMenu(menuButton, menu) {
        // position menu based on location of menu button
        let menuButtonOffset = $(menuButton).offset();
        let menuHeight = $(menu).height();
        let menuWidth = $(menu).width();
        let menuTop = menuButtonOffset.top;
        let menuBottom = menuTop + menuHeight;
        let menuLeft = menuButtonOffset.left + 15;
         // now account for possibility of menu being positioned below the view area (in which case we will align the bottom of the menu with the bottom to avoid running off the bottom of screen)
        if (menuBottom > $(window).height()) {
            menuTop = menuTop - menuHeight + 20;
        }
        if ((menuLeft + menuWidth) > $(window).width()) {
            menuLeft = menuLeft - menuWidth - 10;
        }

        $(menu).offset({ top: menuTop, left: menuLeft });
        _logDebug('_positionGridRowMenu()', { menuButton: menuButton, menu: menu, menuButtonOffset: menuButtonOffset, menuOffset: $(menu).offset() });
    }
})();
